/**
 * TanStack Query hook for the projects list.
 *
 * Replaces `useProjectsStore`'s `projects` Loadable + `loadProjects`
 * imperative action with a declarative query that auto-fetches on
 * mount and shares its result across every consumer (sidebar
 * `ProjectSwitcher`, `Breadcrumbs`, project-picker page, etc.).
 *
 * Follows the visibility-domain template established by
 * `useBrandPrompts` / `useVisibilityStats`:
 * - queryFn does fetch + light shape mapping
 * - returns raw `UseQueryResult` (no extra wrapping — the v5
 *   discriminated union IS the Loadable the legacy store hand-rolled)
 * - relies on the global staleTime (5 min) set in `app/Providers.tsx`
 *
 * Unlike the visibility slices this is not project-scoped: the API
 * returns every project the caller can see (via RLS), so the query
 * key has no projectId input. Auth state IS implicit — if the user
 * isn't authenticated, the fetch returns 401 and the query enters the
 * error state, same as before.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ProjectListResponse } from "@ripenn/api-contracts";
import { safeFetch } from "@/lib/api/safe-fetch";
import { buildApiUrl } from "@/lib/api-config";
import { projectKeys } from "@/lib/queryKeys";
import type { Project } from "@/lib/projects/types";

async function fetchProjects(): Promise<Project[]> {
  const data = await safeFetch(
    ProjectListResponse,
    buildApiUrl("/api/projects"),
    { method: "GET" },
  );
  return data.projects;
}

export function useProjects(): UseQueryResult<Project[], Error> {
  return useQuery({
    queryKey: projectKeys.list(),
    queryFn: fetchProjects,
  });
}
